import { useRef, useState } from "react";
import {
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  Pressable,
  ScrollView,
  Text,
  TextInput,
  View,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { createFileRoute, Navigate, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";

import { LogoMark } from "@/components/Logo";
import { CadencePicker } from "@/components/CadencePicker";
import { COMPOUND_PRESETS } from "@/lib/pk/presets";
import { useCompounds } from "@/lib/state/compounds";
import { db } from "@/lib/db/instant";
import { formatDaysShort } from "@/lib/format";
import { cn } from "@/lib/utils/cn";
import { doseAmountToMg, formatDoseAmount } from "@/lib/forms/dose";
import { useDoseSubmission } from "@/lib/forms/useDoseSubmission";

export const Route = createFileRoute("/onboarding")({
  component: OnboardingScreen,
});

const STEPS = ["compound", "cadence", "mix", "review"] as const;
type Step = (typeof STEPS)[number];

const COMMON_CONCENTRATIONS: Record<string, number[]> = {
  ester: [250, 200, 100],
  glp1: [20, 10, 5],
  custom: [100],
};

function OnboardingScreen() {
  const { isLoading, user } = db.useAuth();
  const { createCompound } = useCompounds();
  const navigate = useNavigate();
  const { submitting, error, submit } = useDoseSubmission(() => {
    void navigate({ to: "/home", replace: true });
  });

  const [step, setStep] = useState<Step>("compound");
  const [compoundId, setCompoundId] = useState<string | null>(null);
  const [concentrationText, setConcentrationText] = useState("");
  const [defaultDoseText, setDefaultDoseText] = useState("");
  const [cadenceHours, setCadenceHours] = useState(168);
  const submitStart = useRef<Date | null>(null);

  if (isLoading) {
    return (
      <View className="flex-1 items-center justify-center bg-bg">
        <ActivityIndicator color="#FF5A1F" />
      </View>
    );
  }
  if (!user) return <Navigate to="/sign-in" />;

  const preset = COMPOUND_PRESETS.find((p) => p.id === compoundId);
  const stepIdx = STEPS.indexOf(step);

  const concentration = doseAmountToMg(concentrationText, "mg", 1);
  const defaultDose = doseAmountToMg(defaultDoseText, "mg", 1);

  const canContinue =
    (step === "compound" && !!compoundId) ||
    (step === "mix" &&
      Number.isFinite(concentration) &&
      Number.isFinite(defaultDose)) ||
    (step === "cadence" &&
      Number.isFinite(cadenceHours) &&
      cadenceHours >= 1 &&
      cadenceHours <= 8760) ||
    (step === "review" &&
      !!preset &&
      Number.isFinite(concentration) &&
      Number.isFinite(defaultDose) &&
      Number.isFinite(cadenceHours));

  const goNext = () => {
    if (!canContinue || submitting) return;
    if (step === "review") {
      if (!compoundId) return;
      void submit(async () => {
        await createCompound({
          compoundId,
          concentrationMgPerMl: concentration,
          defaultDoseMg: defaultDose,
          cadenceHours,
          scheduleStartsAt:
            submitStart.current ?? (submitStart.current = new Date()),
        });
      });
      return;
    }
    setStep(STEPS[stepIdx + 1]!);
  };

  const goBack = () => {
    if (submitting) return;
    if (stepIdx === 0) {
      navigate({ to: "/" });
      return;
    }
    setStep(STEPS[stepIdx - 1]!);
  };

  return (
    <SafeAreaView className="flex-1 bg-bg">
      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : undefined}
        className="flex-1"
      >
        <View className="flex-row items-center justify-between px-5 pb-3 pt-3">
          <Pressable
            onPress={goBack}
            disabled={submitting}
            hitSlop={12}
            className="active:opacity-60"
          >
            <ArrowLeft color="#52525B" size={18} />
          </Pressable>
          <View className="flex-row gap-1">
            {STEPS.map((s, i) => (
              <View
                key={s}
                className={cn(
                  "h-1 w-8 rounded-full",
                  i <= stepIdx ? "bg-accent" : "bg-border",
                )}
              />
            ))}
          </View>
          <View style={{ width: 18 }} />
        </View>

        <ScrollView
          className="flex-1"
          contentContainerStyle={{
            paddingHorizontal: 24,
            paddingTop: 8,
            paddingBottom: 16,
          }}
          showsVerticalScrollIndicator={false}
        >
          {step === "compound" && (
            <View>
              <LogoMark size={40} variant="dark" />
              <Text className="mt-4 text-2xl font-semibold tracking-tight text-text">
                Pick your compound
              </Text>
              <Text className="mt-1.5 text-sm text-textDim">
                You can add more later from the switcher.
              </Text>

              <View className="mt-6 gap-2">
                {COMPOUND_PRESETS.map((p) => {
                  const isOn = compoundId === p.id;
                  return (
                    <Pressable
                      key={p.id}
                      onPress={() => setCompoundId(p.id)}
                      className={cn(
                        "flex-row items-center justify-between rounded-2xl border p-4 active:opacity-80",
                        isOn
                          ? "border-accent bg-accent/5"
                          : "border-border bg-surface",
                      )}
                    >
                      <View className="flex-1">
                        <Text className="text-base font-medium text-text">
                          {p.name}
                        </Text>
                        <Text className="mt-0.5 text-xs text-textMuted capitalize">
                          {p.kind} · t½ {p.absorptionHalfLifeHours}h
                        </Text>
                      </View>
                      {isOn && <Check color="#FF5A1F" size={18} />}
                    </Pressable>
                  );
                })}
              </View>
            </View>
          )}

          {step === "cadence" && preset && (
            <View>
              <Text className="text-2xl font-semibold tracking-tight text-text">
                How often?
              </Text>
              <Text className="mt-1.5 text-sm text-textDim">
                Your default injection schedule. halvn keeps you on this curve
                and lets you deviate.
              </Text>

              <View className="mt-6">
                <CadencePicker
                  valueHours={cadenceHours}
                  onChange={setCadenceHours}
                />
              </View>
            </View>
          )}

          {step === "mix" && preset && (
            <View>
              <Text className="text-2xl font-semibold tracking-tight text-text">
                Your mix
              </Text>
              <Text className="mt-1.5 text-sm text-textDim">
                Concentration is how much {preset.shortCode} is in each mL of
                solution. Default dose is what you usually take per injection.
              </Text>

              <Text className="mb-1.5 mt-5 text-[10px] font-medium uppercase tracking-wider text-textMuted">
                Concentration
              </Text>
              <View className="flex-row items-center rounded-xl border border-border bg-surface px-3 py-3">
                <TextInput
                  value={concentrationText}
                  onChangeText={setConcentrationText}
                  keyboardType="decimal-pad"
                  placeholder="e.g. 250"
                  placeholderTextColor="#A1A1AA"
                  className="flex-1 text-base text-text outline-none"
                  selectTextOnFocus
                />
                <Text className="text-sm text-textMuted">mg/mL</Text>
              </View>
              <View className="mt-2 flex-row flex-wrap gap-1.5">
                {(
                  COMMON_CONCENTRATIONS[preset.kind] ??
                  COMMON_CONCENTRATIONS.custom!
                ).map((c) => (
                  <Pressable
                    key={c}
                    onPress={() => setConcentrationText(String(c))}
                    className="rounded-full border border-border bg-surface px-3 py-1.5 active:opacity-70"
                  >
                    <Text className="text-xs text-textDim">{c} mg/mL</Text>
                  </Pressable>
                ))}
              </View>

              <Text className="mb-1.5 mt-5 text-[10px] font-medium uppercase tracking-wider text-textMuted">
                Default dose per injection
              </Text>
              <View className="flex-row items-center rounded-xl border border-border bg-surface px-3 py-3">
                <TextInput
                  value={defaultDoseText}
                  onChangeText={setDefaultDoseText}
                  keyboardType="decimal-pad"
                  placeholder={preset.kind === "ester" ? "e.g. 80" : "e.g. 5"}
                  placeholderTextColor="#A1A1AA"
                  className="flex-1 text-base text-text outline-none"
                  selectTextOnFocus
                />
                <Text className="text-sm text-textMuted">mg</Text>
              </View>
              {defaultDose > 0 && concentration > 0 && (
                <Text className="mt-2 text-xs text-textMuted">
                  {formatDoseAmount(defaultDose, concentration, "u")} units on a
                  U-100 syringe
                </Text>
              )}
            </View>
          )}

          {step === "review" && preset && (
            <View>
              <Text className="text-2xl font-semibold tracking-tight text-text">
                Confirm setup
              </Text>
              <Text className="mt-1.5 text-sm text-textDim">
                You can edit any of this later from Profile.
              </Text>

              <View className="mt-6 rounded-2xl border border-border bg-surface p-4">
                <ReviewRow label="Compound" value={preset.name} />
                <ReviewRow
                  label="Concentration"
                  value={`${concentration} mg/mL`}
                />
                <ReviewRow
                  label="Default dose"
                  value={`${defaultDose} mg · ${formatDoseAmount(defaultDose, concentration, "u")} U-100 units`}
                />
                <ReviewRow
                  label="Cadence"
                  value={`every ${formatDaysShort(cadenceHours)}`}
                />
              </View>

              <Text className="mt-4 text-[11px] text-textMuted">
                Your schedule starts today. Setup does not log an injection; add
                the doses you actually took, and edit the schedule time in
                Profile when needed.
              </Text>
            </View>
          )}
        </ScrollView>

        <View className="px-6 pb-6">
          {error && (
            <Text role="alert" className="mb-3 text-sm text-danger">
              {error}
            </Text>
          )}
          <Pressable
            onPress={goNext}
            disabled={!canContinue || submitting}
            className={cn(
              "flex-row items-center justify-center gap-2 rounded-xl py-4",
              canContinue ? "bg-accent active:opacity-80" : "bg-borderHi",
            )}
          >
            <Text className="text-sm font-medium text-white">
              {submitting
                ? "Saving…"
                : step === "review"
                  ? "Finish setup"
                  : "Continue"}
            </Text>
            <ArrowRight color="#FFFFFF" size={16} />
          </Pressable>
        </View>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

function ReviewRow({ label, value }: { label: string; value: string }) {
  return (
    <View className="flex-row items-center justify-between border-b border-border/60 py-2.5 last:border-b-0">
      <Text className="text-sm text-textDim">{label}</Text>
      <Text className="text-sm text-text">{value}</Text>
    </View>
  );
}
